// SEQ-240: everything the "How is this worked out" pop-up decides, as pure
// functions, so a Deno test can pin the behaviour without a DOM.
//
// HowWorkedOut.tsx stays thin: it renders, measures and wires events, and asks
// this module what to do. Covered by howWorkedOutModel.test.ts:
// - open and close transitions, and that only one pop-up is open at a time
// - which close reasons hand focus back to the trigger
// - phone sheet versus anchored pop-over
// - pop-over placement: flip above or below, clamp inside the viewport
// - dialog aria ids and the trigger's accessible name
// - the focus trap's next index
// - the rows the dialog shows, including the "Right now" line, which goes
//   through formatMetricValue so Safe Mode masks money exactly as the page does

import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import {
  DATE_SCOPE_LABELS,
  PROJECT_SCOPE_LABELS,
  SOURCE_KIND_LABELS,
  type MetricDefinition,
  type RightNowValue,
} from "./metricDefinition";

// ─── Open state ──────────────────────────────────────────────────────────────

/** Why a pop-up closed. Decides whether focus goes back to its trigger. */
export type CloseReason = "escape" | "outside" | "trigger" | "close-button" | "replaced";

export interface PopState {
  /** The one open pop-up, or null. */
  openId: string | null;
  /** The last pop-up that closed and why, so its trigger can take focus back. */
  lastClosed: { id: string; reason: CloseReason } | null;
}

export type PopEvent =
  | { type: "toggle"; id: string }
  | { type: "close"; id: string; reason: CloseReason };

const INITIAL_POP_STATE: PopState = { openId: null, lastClosed: null };

export function reducePopState(state: PopState, event: PopEvent): PopState {
  if (event.type === "toggle") {
    if (state.openId === event.id) {
      return { openId: null, lastClosed: { id: event.id, reason: "trigger" } };
    }
    // Opening one closes any other: only one is ever open.
    return {
      openId: event.id,
      lastClosed: state.openId ? { id: state.openId, reason: "replaced" } : state.lastClosed,
    };
  }
  // A close for a pop-up that is not the open one is stale (it was already
  // replaced), so it changes nothing.
  if (state.openId !== event.id) return state;
  return { openId: null, lastClosed: { id: event.id, reason: event.reason } };
}

/**
 * Focus goes back to the trigger after Escape or the close button. Not after
 * a click elsewhere (the reader chose where to go next) and not when another
 * pop-up replaced this one (focus moves into the new one).
 */
export function shouldReturnFocus(reason: CloseReason): boolean {
  return reason === "escape" || reason === "close-button" || reason === "trigger";
}

/** A pointer press: inside the panel or on the trigger changes nothing here; anywhere else closes. */
export function closeReasonForPointerDown(insidePanel: boolean, onTrigger: boolean): CloseReason | null {
  if (insidePanel || onTrigger) return null;
  return "outside";
}

export function isCloseKey(key: string): boolean {
  return key === "Escape" || key === "Esc";
}

/** A tiny store shared by every pop-up on the page, read through useSyncExternalStore. */
export interface PopStore {
  getState(): PopState;
  dispatch(event: PopEvent): void;
  subscribe(listener: () => void): () => void;
}

export function createPopStore(initial: PopState = INITIAL_POP_STATE): PopStore {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    getState: () => state,
    dispatch(event) {
      const next = reducePopState(state, event);
      if (next === state) return;
      state = next;
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// ─── Layout ──────────────────────────────────────────────────────────────────

/** At or below this width the pop-up is a bottom sheet; above it, an anchored pop-over. */
const SHEET_MAX_WIDTH = 640;

export type PopMode = "sheet" | "popover";

export function choosePopMode(viewportWidth: number): PopMode {
  return viewportWidth <= SHEET_MAX_WIDTH ? "sheet" : "popover";
}

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Placement {
  top: number;
  left: number;
  width: number;
  /** The panel scrolls inside this height when its content is taller. */
  maxHeight: number;
  side: "below" | "above";
  /** Where the trigger sits relative to the panel, for transform-origin. */
  originX: number;
  originY: number;
}

const POPOVER_GAP = 8;
const VIEWPORT_MARGIN = 12;
/** The panel never shrinks below this, even in a very short window. */
const MIN_PANEL_HEIGHT = 120;

/**
 * Places the pop-over under its trigger, or above it when there is more room
 * there and the panel does not fit below. Always inside the viewport, with a
 * margin; a panel taller than the room gets a max height and scrolls inside.
 */
export function placePopover(
  trigger: Box,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  gap: number = POPOVER_GAP,
  margin: number = VIEWPORT_MARGIN,
): Placement {
  const width = Math.min(panel.width, Math.max(0, viewport.width - margin * 2));
  const triggerBottom = trigger.top + trigger.height;
  const roomBelow = viewport.height - triggerBottom - gap - margin;
  const roomAbove = trigger.top - gap - margin;

  const side: Placement["side"] = panel.height <= roomBelow || roomBelow >= roomAbove ? "below" : "above";
  const room = side === "below" ? roomBelow : roomAbove;
  const maxHeight = Math.max(MIN_PANEL_HEIGHT, Math.min(room, viewport.height - margin * 2));
  const height = Math.min(panel.height, maxHeight);

  let top = side === "below" ? triggerBottom + gap : trigger.top - gap - height;
  top = clamp(top, margin, Math.max(margin, viewport.height - margin - height));

  const triggerCentre = trigger.left + trigger.width / 2;
  const left = clamp(triggerCentre - width / 2, margin, Math.max(margin, viewport.width - margin - width));

  return {
    top,
    left,
    width,
    maxHeight,
    side,
    originX: clamp(triggerCentre - left, 0, width),
    originY: side === "below" ? 0 : height,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

// ─── Accessibility ───────────────────────────────────────────────────────────

interface HowWorkedOutIds {
  dialog: string;
  title: string;
  description: string;
}

/** Stable ids for one pop-up: the dialog, its label (the figure name) and its description (the meaning). */
export function howWorkedOutIds(baseId: string): HowWorkedOutIds {
  const safe = baseId.replace(/[^A-Za-z0-9_-]/g, "");
  return {
    dialog: `hwo-${safe}-dialog`,
    title: `hwo-${safe}-title`,
    description: `hwo-${safe}-meaning`,
  };
}

/**
 * The trigger's accessible name. `where` tells two triggers with the same
 * figure name apart (Net profit on the P&L card and in the month on month
 * table), for example "in the P&L overview".
 */
export function triggerLabel(figureName: string, where?: string): string {
  return where ? `How is ${figureName} ${where} worked out` : `How is ${figureName} worked out`;
}

/** The focus trap: Tab from the last control goes to the first, Shift+Tab from the first to the last. */
export function nextTrapIndex(current: number, count: number, backwards: boolean): number {
  if (count <= 0) return -1;
  if (current < 0) return backwards ? count - 1 : 0;
  return backwards ? (current - 1 + count) % count : (current + 1) % count;
}

// ─── Content ─────────────────────────────────────────────────────────────────

export interface DefinitionRow {
  key: "meaning" | "formula" | "source" | "scope" | "period" | "right-now" | "last-updated";
  label: string;
  lines: string[];
}

const ROW_LABELS: Record<DefinitionRow["key"], string> = {
  meaning: "What it means",
  formula: "How it is worked out",
  source: "Where the data comes from",
  scope: "Which projects and dates",
  period: "Period",
  "right-now": "Right now",
  "last-updated": "Last updated",
};

/** "Right now" text for one value, through the page's own formatter, so Safe Mode masks money and nothing else. */
export function formatRightNow(item: RightNowValue): string {
  return `${item.label}: ${formatMetricValue(item.value, item.unit, undefined, { nullPlaceholder: "not available yet" })}`;
}

/** The rows the dialog shows, in order. Right now and Last updated are left out unless there is something real to show. */
export function buildDefinitionRows(def: MetricDefinition, rightNow?: readonly RightNowValue[]): DefinitionRow[] {
  const rows: DefinitionRow[] = [
    { key: "meaning", label: ROW_LABELS.meaning, lines: [def.meaning] },
    { key: "formula", label: ROW_LABELS.formula, lines: [...def.formula] },
    {
      key: "source",
      label: ROW_LABELS.source,
      lines: [`${SOURCE_KIND_LABELS[def.source.kind]}. ${def.source.detail}`],
    },
    {
      key: "scope",
      label: ROW_LABELS.scope,
      lines: [
        joinScope(PROJECT_SCOPE_LABELS[def.projects.scope], def.projects.detail),
        joinScope(DATE_SCOPE_LABELS[def.dates.scope], def.dates.detail),
      ],
    },
    { key: "period", label: ROW_LABELS.period, lines: [def.period] },
  ];
  if (rightNow && rightNow.length > 0) {
    rows.push({ key: "right-now", label: ROW_LABELS["right-now"], lines: rightNow.map(formatRightNow) });
  }
  if (def.lastUpdated) {
    rows.push({ key: "last-updated", label: ROW_LABELS["last-updated"], lines: [def.lastUpdated] });
  }
  return rows;
}

function joinScope(label: string, detail?: string): string {
  return detail ? `${label} ${detail}` : label;
}
